import { create } from 'zustand';

import { useAuthStore } from '@/modules/auth/stores/useAuthStore';

import { coreApi } from '../services/api';

async function fetchAllLocationCollections(
  url: string | null = '/api/location-collection/',
  acc: Project[] = []
): Promise<Project[]> {
  if (!url) return acc;
  const response = await coreApi.fetchLocationCollections(url);
  return fetchAllLocationCollections(response.next, [...acc, ...(response.results || [])]);
}

import type { CoreState, Organization, Project } from '../types';

export const useCoreStore = create<CoreState>((set, get) => ({
  organizations: [],
  currentOrganization: null,
  projects: [],
  loading: false,
  // Tells "not fetched yet" from "no projects", which `loading` (initially false) cannot.
  projectsSettled: false,

  fetchOrganizations: async () => {
    set({ loading: true });
    try {
      const response = await coreApi.fetchOrganizations();
      const organizations = response.results || [];

      set({ organizations });

      // Check if user already has an organization assigned
      const { user } = useAuthStore.getState();
      const userOrgName = user?.organisation;

      if (userOrgName) {
        // Find the organization by name and set it directly without API call
        const matchingOrg = organizations.find((org) => org.name === userOrgName);
        if (matchingOrg) {
          set({ currentOrganization: matchingOrg, loading: false });
          // Fetch location collections for this organization
          await get().fetchLocationCollections();
          return;
        }
      }

      // Fallback: Set the first organization as active (with API call)
      const defaultOrg = organizations[0];
      if (defaultOrg) {
        await get().setActiveOrganization(defaultOrg.id);
      } else {
        // No organisation to fetch projects for, so there will never be any.
        set({ loading: false, projectsSettled: true });
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      set({ loading: false, projectsSettled: true });
    }
  },

  setCurrentOrganization: async (org: Organization) => {
    await get().setActiveOrganization(org.id);
  },

  setActiveOrganization: async (orgId: number) => {
    try {
      await coreApi.setActiveOrganization(orgId);
      // Reload page and clear query params when organization changes
      window.location.href = window.location.pathname;
    } catch (error) {
      console.error('Failed to set active organization:', error);
      throw error;
    }
  },

  fetchLocationCollections: async () => {
    if (get().projects.length > 0) return;
    set({ loading: true });
    try {
      const allProjects = await fetchAllLocationCollections();
      set({ projects: allProjects, loading: false, projectsSettled: true });
    } catch (error) {
      console.error('Failed to fetch location collections:', error);
      set({ loading: false, projectsSettled: true });
      throw error;
    }
  },
}));
